export enum LocationType {
  GLOBAL = 'GLOBAL',
  COUNTRY = 'COUNTRY',
  STATE = 'STATE',
  CITY = 'CITY',
  NEIGHBORHOOD = 'NEIGHBORHOOD',
  REGION = 'REGION'
}

export type ExtendedLocationType = LocationType | 'PLACE';
export interface UserSettings {
  allowMultiplePromotes?: boolean;
  [key: string]: any;
}

// New Privacy System Types
export interface PrivacyField {
  globalPrivacy: 'everyone' | 'friends' | 'none';
  allowedUsers: string[]; // ["userId1", "userId2.blocked"]
}

export interface TagPrivacyField extends PrivacyField {
  tagSpecific?: {
    [tagName: string]: {
      allowedUsers: string[];
    };
  };
}

export interface UserPrivacy {
  // Profile fields
  displayName: PrivacyField;
  bio: PrivacyField;
  facePic: PrivacyField;
  
  // Identity fields
  birthday: PrivacyField;
  gender: PrivacyField;
  race: PrivacyField;
  sexualOrientation: PrivacyField;
  
  // Location fields
  homeNeighborhood: PrivacyField;
  
  // Tags with advanced privacy
  tags: TagPrivacyField;
}

export interface User {
  _id: string;
  displayName: string;
  username: string;
  email: string;
  phone?: string;
  isPhoneVerified?: boolean;
  isEmailVerified?: boolean;
  authProvider?: 'local' | 'google' | 'apple' | 'phone';
  profilePicture?: string;
  facePicture?: string;
  publicPicture?: string;
  bio?: string;
  customStatus?: string;
  
  // Raw data fields (backend stores actual values)
  birthday?: Date | string | null;
  gender?: string;
  race?: string;
  sexualOrientation?: string;
  tags?: string[];
  
  // New Privacy System
  privacy?: UserPrivacy;
  
  // Computed fields (with isPrivate flags for display)
  computedBirthday?: {
    date: Date | string | null;
    isPrivate: boolean;
  };
  computedGender?: {
    content: string;
    isPrivate: boolean;
  };
  computedRace?: {
    content: string;
    isPrivate: boolean;
  };
  computedSexualOrientation?: {
    content: string;
    isPrivate: boolean;
  };
  computedTags?: Array<{
    content: string;
    isPrivate: boolean;
  }>;
  
  homeNeighborhood?: string | Location;
  location?: {
    type: 'Point';
    coordinates: [number, number];
  };
  settings?: Map<string, any> | {
    allowMultiplePromotes?: boolean;
    [key: string]: any;
  };
  expoPushToken?: string;
  mapboxId?: string;
  currentPlace?: string | Place;
  currentNeighborhood?: string | Location;
  currentCity?: string | Location;
  currentState?: string | Location;
  currentCountry?: string | Location;
  friends: string[];
  friendRequests: string[];
  isAdmin: boolean;
  joinedPlaces: string[] | Place[];
  role: 'user' | 'admin';
  isMockUser?: boolean;
  posts: string[];
  pinnedPosts: string[];
  isOnline: boolean;
  lastActive: string | Date;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  blockedUsers: Array<{
    user: string;
    duration: Date | null;
    createdAt: Date;
  }>;
  notificationPreferences?: {
    privateInfoAccess: boolean;
  };
  favoriteLocations?: string[];
}

export interface Location {
  _id: string;
  name: string;
  level: LocationType | 'Country' | 'State' | 'City' | 'Neighborhood';
  parent?: string | Location;
  photo?: string | null;
  population?: number;
  isTown?: boolean;
  totalPosts?: number;
  totalPromotes?: number;
  averagePromotesPerPost?: number;
  onlineUsers?: number;
  totalUsers?: number;
  coordinates?: {
    type: 'Point';
    coordinates: [number, number];
  };
  boundingBox?: {
    type: 'Polygon';
    coordinates: [[[number, number]]];
  };
  adminBoundingBox?: boolean;
  borough?: 'Manhattan' | 'Brooklyn' | 'Queens' | 'The Bronx' | 'Staten Island';
  children?: (Location | Place)[];
}

export interface Place {
  _id: string;
  name: string;
  description?: string;
  creator: string;
  password?: string;
  location: {
    type: 'Point';
    coordinates: [number, number];
  };
  boundingBox?: {
    type: 'Polygon';
    coordinates: [[[number, number]]];
  };
  address?: string;
  phoneNumber?: string;
  neighborhood?: string | Location;
  city?: string | Location;
  state?: string | Location;
  country?: string | Location;
  joinedUsers: string[];
  createdAt: string;
  status: 'public' | 'private' | 'invite-only';
  photo?: string;
  userCount: number;
  currentUsers: string[];
  onlineUsers: string[];
  joinRequests: string[];
  invitations: string[];
  type: string;
  subtypes?: string[];
  tags?: string[];
  entryRadiusFeet?: number;
  isOfficial?: boolean;
  isBusiness?: boolean;
  officialStatus?: 'none' | 'pending' | 'verified' | 'admin';
  officialVerificationDate?: string | null;
  importSource?: 'user_created' | 'google_places' | 'manual' | 'mta_import';
  googlePlaceId?: string;
  hasMenu?: boolean;
}

export interface Post {
  _id: string;
  user: string | {
    _id: string;
    displayName: string;
    profilePicture?: string | null;
  };
  text: string;
  content?: string;
  media?: {
    type: 'photo' | 'video' | null;
    photo?: string;
    video?: string;
    thumbnail?: string;
    width: number;
    height: number;
    aspectRatio: number;
    isVisible: boolean;
  };
  location: {
    country?: { id: string; name: string };
    state?: { id: string; name: string };
    city?: { id: string; name: string };
    neighborhood?: { id: string; name: string };
    place?: { id: string; name: string };
  };
  originLevel: ExtendedLocationType;
  currentPostLevel: ExtendedLocationType;
  highestPromotedLevel?: ExtendedLocationType | null;
  visibilityLevels: Array<{
    level: ExtendedLocationType;
    locationId: string;
  }>;
  isVisible: boolean;
  totalPromotes: number;
  levelPromotes: number;
  promotePercentage: number;
  promoteDisplay?: string;
  promotes: string[];
  comments: any[];
  shares: number;
  interactionScore: number;
  // Lifetime engagement counters (product-analytics events pipeline).
  impressionCount?: number;
  viewCount?: number;
  detailOpenCount?: number;
  photoOpenCount?: number;
  videoOpenCount?: number;
  hashtags: string[];
  isPinned: boolean;
  isComment: boolean;
  parentPost?: string;
  createdAt: string;
  expiresAt?: string | null;
  showUserNeighborhood: boolean;
  isMockPost?: boolean;
}

export interface Option {
  id: string;
  value: string;
}

export interface IdentityOptions {
  gender: Option[];
  race: Option[];
  sexualOrientation: Option[];
}

export interface Notification {
  _id: string;
  recipient: string;
  sender: string;
  type: string;
  content: string;
  reference?: string;
  expoPushToken?: string;
  metadata?: Record<string, any>;
  read: boolean;
  active: boolean;
  createdAt: string;
}

export interface NotificationTestResponse {
  success: boolean;
  message: string;
  notification?: Notification;
  notifications?: Notification[];
  unreadCount?: number;
}

export interface ExpirationSettings {
  types: {
    '30m': boolean;
    '1h': boolean;
    '6h': boolean;
    '12h': boolean;
    '24h': boolean;
    '48h': boolean;
    'infinity': boolean;
  };
  chances: {
    '30m': number;
    '1h': number;
    '6h': number;
    '12h': number;
    '24h': number;
    '48h': number;
    'infinity': number;
  };
}

export interface CreateMockPostsRequest {
  count?: number;
  withPhotos?: boolean;
  withVideos?: boolean;
  photoChance?: number;
  videoChance?: number;
  mediaFolder?: string;
  enabledPhotoCategories?: string[];
  withPromotes?: boolean;
  promoteChance?: number;
  maxPromotesPerPost?: number;
  maxPromotionLevel?: 'PLACE' | 'NEIGHBORHOOD' | 'CITY' | 'STATE' | 'COUNTRY';
  promotionLevelChances?: Record<string, number>;
  withComments?: boolean;
  commentChance?: number;
  maxCommentsPerPost?: number;
  selectedNeighborhoods?: Neighborhood[];
  selectedPlaces?: Place[];
  selectedPlaceType?: string;
  expirationSettings?: ExpirationSettings;
  savedMockPosts?: Array<{
    _id: string;
    text: string;
    locationId: string;
    locationType: 'place' | 'neighborhood' | 'place-type';
  }>;
  useSavedPostText?: boolean;
  enableRateLimit?: boolean;
  durationMinutes?: number; // Time period to spread posts over
}

export interface CreateMockPostsResponse {
  success: boolean;
  message: string;
  posts: any[];
  postsCreated?: number;
  jobId?: string;
  job?: MockPostJobStatus;
}

export interface MockPostJobStatus {
  jobId: string;
  status: 'running' | 'completed' | 'cancelled' | 'error';
  totalPosts: number;
  createdPosts: number;
  startTime: string;
  endTime?: string;
  durationMinutes: number;
  estimatedCompletionTime?: string;
  progress: number; // 0-100
  error?: string;
}

export interface Neighborhood {
  _id: string;
  name: string;
  level: 'Neighborhood';
  parent?: string | Location;
  coordinates?: {
    type: 'Point';
    coordinates: number[];
  };
  boundingBox?: {
    type: 'Polygon';
    coordinates: number[][][];
  };
}

export interface GlobalSettings {
  _id?: string;
  __v?: number;
  locationUpdateInterval: number;
  promoteThreshold: number;
  allowMultiplePromotes: boolean;
  searchRadius: number;
  placeVisibilityRadius: number;
  placeEntryRadius: number;
  placeSearchLimit: number;
  placePhotoMaxWidth: number;
  usersPerPage: number;
  useDefaultUserCounts: boolean;
  defaultUserCounts: {
    neighborhood: number;
    city: number;
    state: number;
    country: number;
  };
  userStatusThresholds: {
    online: number;
    recentlyActive: number;
    inactive: number;
  };
  aggregateCityNeighborhoods: boolean;
}

// Note: This interface might be redundant with the Location interface
// when level is 'Neighborhood', but keeping for backward compatibility
export interface Neighborhood {
  _id: string;
  name: string;
  cityName?: string;
  coordinates?: {
    type: 'Point';
    coordinates: number[];
  };
}

export interface PostStats {
  PLACE: number;
  NEIGHBORHOOD: number;
  CITY: number;
  STATE: number;
  COUNTRY: number;
  REGION: number;
} 